import React from "react";
import Modal from "react-modal";
import { X } from "react-feather";
import { useFormik } from "formik";
import * as Yup from "yup";
import SelectVendor from "../../../components/Formik/SelectVendor";
import SelectBroker from "../../../components/Formik/SelectBroker";
import CustomDatePicker from "../../../components/Formik/CustomDatePicker";
import {adminExportReport} from "../../../services/api/admin";

const ReportExportModal = ({ report, onClose, onExport }) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const initialValues = {
        broker_ids: [],
        vendor_ids: [],
        start_date: '',
        end_date: '',
    };

    const validationShape = {};
    if (report.filters.broker !== undefined) {
        validationShape.broker_ids = Yup.array().min(1, 'At least one broker is required');
    }
    if (report.filters.vendor !== undefined) {
        validationShape.vendor_ids = Yup.array().min(1, 'At least one vendor is required');
    }
    if (report.filters.startDate !== undefined) {
        validationShape.start_date = Yup.date().required('Start date is required');
    }
    if (report.filters.endDate !== undefined) {
        validationShape.end_date = Yup.date()
            .required('End date is required')
            .min(Yup.ref('start_date'), 'End date must be after the start date');
    }

    const formik = useFormik({
        initialValues,
        validationSchema: Yup.object().shape(validationShape),
        onSubmit: async (values) => {
            try {
                setIsSubmitting(true);

                await adminExportReport({
                    type: report.type,
                    broker_id: values.broker_ids.length > 0 ? values.broker_ids.map(b => b.id) : undefined,
                    vendor_id: values.vendor_ids.length > 0 ? values.vendor_ids.map(v => v.id) : undefined,
                    start_date: values.start_date ?? undefined,
                    end_date: values.end_date ?? undefined,
                });

                onExport(report.type, values);
                setIsSubmitting(false);
                onClose();
            } catch (e) {
                setIsSubmitting(false);
                console.log("Export error", e);
            }
        },
    });

    const handleBrokerSelect = (broker) => {
        const currentBrokers = formik.values.broker_ids || [];
        const isAlreadySelected = currentBrokers.some(b => b.id === broker.id);

        if (isAlreadySelected) {
            // Remove if already selected
            const updatedBrokers = currentBrokers.filter(b => b.id !== broker.id);
            formik.setFieldValue('broker_ids', updatedBrokers);
        } else {
            // Add if not selected
            const updatedBrokers = [...currentBrokers, broker];
            formik.setFieldValue('broker_ids', updatedBrokers);
        }
    };

    const handleVendorSelect = (vendor) => {
        const currentVendors = formik.values.vendor_ids || [];
        const isAlreadySelected = currentVendors.some(v => v.id === vendor.id);

        if (isAlreadySelected) {
            // Remove if already selected
            const updatedVendors = currentVendors.filter(v => v.id !== vendor.id);
            formik.setFieldValue('vendor_ids', updatedVendors);
        } else {
            // Add if not selected
            const updatedVendors = [...currentVendors, vendor];
            formik.setFieldValue('vendor_ids', updatedVendors);
        }
    };

    return (
        <Modal
            isOpen={true}
            className="sl-modal-content sl-max-modal"
            overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50"
        >
            <div className="sl-modal-body">
                <div className="sl-modal-header sl-rounded-md sl-flex sl-justify-between sl-bg-gray-50 sl-text-gray-900 sl-px-6 sl-py-16 sl-border-bottom">
                    <div className="title sl-font-semibold sl-text-lg">
                        Export Report
                    </div>
                    <div className="sl-cursor-pointer sl-btn-link-gray">
                        <X onClick={onClose} />
                    </div>
                </div>

                <div className="sl-modal-body sl-px-6 sl-overflow-y-auto sl-max-h-162 sl-customized-scrollbar sl-pt-6">
                    <p className={"sl-mb-0"}>You're about to export the <b>{report.name} Report.</b></p>

                    {(Object.keys(report.filters || {}).length > 0) && (
                        <p className="sl-mt-2 sl-text-sm sl-text-gray-600 sl-mt-6">
                            This report requires additional data. Please complete the fields below before proceeding
                            with the export.
                        </p>
                    )}

                    {(Object.keys(report.filters || {}).length > 0) && (<div className="sl-flex sl-flex-col sl-width-100 sl-mt-6">

                        {report.filters.broker !== undefined && (
                            <div>
                                {formik.values.broker_ids && formik.values.broker_ids.length > 0 && (
                                    <div className="sl-mb-4">
                                        <p className="sl-font sl-font-medium sl-text-sm sl-text-gray-700 ">Selected
                                            Brokers:</p>
                                        <div className="sl-flex sl-flex-wrap sl-gap-2 sl-mt-1">
                                            {formik.values.broker_ids.map(broker => (
                                                <span key={broker.id}
                                                      className="sl-bg-gray-100 sl-text-blue-800 sl-px-4 sl-py-2 sl-rounded sl-text-md">
                                                    {broker.name}
                                                    <button onClick={() => handleBrokerSelect(broker)}
                                                            className="sl-ml-2 sl-font-semibold sl-text-gray-700 hover:sl-text-gray-800">
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <SelectBroker
                                    formik={formik}
                                    label="Select Broker"
                                    name="broker_ids"
                                    setSelectedBroker={handleBrokerSelect}
                                />
                            </div>
                        )}

                        {report.filters.vendor !== undefined && (
                            <div>
                                {formik.values.vendor_ids && formik.values.vendor_ids.length > 0 && (
                                    <div className="sl-mt-2">
                                        <p className="sl-text-sm sl-text-gray-600">Selected Vendors:</p>
                                        <div className="sl-flex sl-flex-wrap sl-gap-2 sl-mt-1">
                                            {formik.values.vendor_ids.map(vendor => (
                                                <span
                                                    key={vendor.id}
                                                    className="sl-bg-green-100 sl-text-green-800 sl-px-2 sl-py-1 sl-rounded sl-text-sm"
                                                >
                                                    {vendor.name}
                                                    <button
                                                        onClick={() => handleVendorSelect(vendor)}
                                                        className="sl-ml-1 sl-text-green-600 hover:sl-text-green-800"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <SelectVendor
                                    formik={formik}
                                    label="Select Vendor"
                                    name="vendor_id"
                                    setSelectedVendor={handleVendorSelect}
                                />
                                {formik.errors.vendor_ids && formik.touched.vendor_ids && (
                                    <div className="sl-text-red-500 sl-text-sm sl-mt-1">
                                        {formik.errors.vendor_ids}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="sl-flex sl-gap-1 sl-justify-space-between">
                            {report.filters.startDate !== undefined && (
                                <CustomDatePicker formik={formik} label="Start Date" name="start_date"/>
                            )}
                            {report.filters.endDate !== undefined && (
                                <CustomDatePicker formik={formik} label="End Date" name="end_date"/>
                            )}
                        </div>
                    </div>)}

                    <p className={"sl-mt-2 sl-text-sm sl-text-gray-600"}>
                        This report <b>does {report.manualWorkRequired ? '' : 'not '}</b> require additional manual work.
                    </p>
                </div>

                <div className="sl-modal-footer sl-px-6 sl-py-32 sl-flex sl-justify-between">
                    <button
                        onClick={onClose}
                        className="sl-btn sl-btn-tertiary sl-btn-lg sl-rounded-lg sl-text-md sl-pl-20 sl-pr-20"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={isSubmitting}
                        onClick={formik.submitForm}
                        className="sl-btn sl-btn-primary sl-btn-lg sl-rounded-lg sl-text-md sl-pl-20 sl-pr-20"
                    >
                        {isSubmitting ? <div className="sl-loader sl-m-auto sl-w-6 sl-h-6" /> : 'Export'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ReportExportModal;